import 'core-js/modules/es.error.to-string.js';
import 'core-js/modules/es.array.concat.js';
import 'core-js/modules/es.array.map.js';
import 'core-js/modules/es.date.to-string.js';
import 'core-js/modules/es.number.constructor.js';
import 'core-js/modules/es.number.is-integer.js';
import 'core-js/modules/es.object.to-string.js';
import 'core-js/modules/es.regexp.exec.js';
import 'core-js/modules/es.regexp.test.js';
import 'core-js/modules/es.regexp.to-string.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useState } from 'react';
import classNames from 'classnames';
import { PanelLeftIcon, ChevronLeftIcon, ChevronRightIcon, PanelRightIcon } from '@cognite/cogs-icons';
import { useControllableState, getThemeClassNames } from '@cognite/cogs-utils';
import { Button } from '../button/button.js';
import { Divider } from '../divider/divider.js';
import { Flex } from '../flex/flex.js';
import 'core-js/modules/es.object.assign.js';
import 'tslib';
import { Input } from '../input/input.js';
import { Select } from '../select/select.js';
import 'core-js/modules/es.array.every.js';
import 'core-js/modules/es.array.filter.js';
import 'core-js/modules/es.array.includes.js';
import 'core-js/modules/es.array.is-array.js';
import 'core-js/modules/es.array.some.js';
import 'core-js/modules/es.string.includes.js';
import 'react-select';
import 'lodash/uniqBy';
import 'core-js/modules/es.function.name.js';
import '@mui/joy';
import 'color';
import { Skeleton } from '../skeleton/skeleton.js';
import { Tooltip } from '../tooltip/tooltip.js';
import { DEFAULT_RANGE_OPTIONS } from './types.js';

var getLastPage = function getLastPage(resultCount, totalCount) {
  return Math.ceil(totalCount / resultCount);
};
var isNumeric = function isNumeric(value) {
  return /^[0-9]+$/.test(value);
};
var validatePage = function validatePage(page, totalPages) {
  if (page < 1) {
    return 1;
  }
  if (page > totalPages) {
    return totalPages;
  }
  return page;
};
/**
 * Pagination
 * =============================================================================
 * The Pagination component enables users to select a specific page from a range of pages.
 * It can also be used to set the number of items per page, which is useful for table views.
 *
 * [Open docs »](https://cognitedata.github.io/cogs.js/?path=/docs/experimental-wip-pagination--base)
 *
 * -----------------------------------------------------------------------------
 * Examples
 * -----------------------------------------------------------------------------
 * ```
 * <Pagination totalCount={200} elevated onPageChange={(page)=>setSomePage(page)}/>
 *
 * ```
 * -----------------------------------------------------------------------------
 * Properties
 * -----------------------------------------------------------------------------
 * @param initialCurrentPage  - Render the button as a block-level element.
 * @param elevated            - Elevated shadow version of pagination
 * @param hideTeleport        - Hide the teleport input
 * @param hideItemsPerPage    - Hide the select to select items per page
 * @param initialRange        - Initial Value in the items per page
 * @param totalPages          - The total number of  pages
 * @param size                - The size of the pagination.
 * @param skeleton            - Show the component as a skeleton loader.
 *
 * [Read the full documentation »](https://cognitedata.github.io/cogs.js/?path=/docs/experimental-wip-pagination--base)
 */
var Pagination = function Pagination(_ref) {
  var currentPageProp = _ref.currentPage,
    _ref$inverted = _ref.inverted,
    inverted = _ref$inverted === undefined ? false : _ref$inverted,
    _ref$initialCurrentPa = _ref.initialCurrentPage,
    initialCurrentPage = _ref$initialCurrentPa === undefined ? 1 : _ref$initialCurrentPa,
    _ref$totalPages = _ref.totalPages,
    totalPages = _ref$totalPages === undefined ? 1 : _ref$totalPages,
    _ref$setItemPerPage = _ref.setItemPerPage,
    setItemPerPage = _ref$setItemPerPage === undefined ? function () {} : _ref$setItemPerPage,
    itemsPerPage = _ref.itemsPerPage,
    _ref$size = _ref.size,
    size = _ref$size === undefined ? 'medium' : _ref$size,
    _ref$elevated = _ref.elevated,
    elevated = _ref$elevated === undefined ? false : _ref$elevated,
    onPageChange = _ref.onPageChange,
    _ref$hideTeleport = _ref.hideTeleport,
    hideTeleport = _ref$hideTeleport === undefined ? false : _ref$hideTeleport,
    _ref$hideItemsPerPage = _ref.hideItemsPerPage,
    hideItemsPerPage = _ref$hideItemsPerPage === undefined ? false : _ref$hideItemsPerPage,
    skeleton = _ref.skeleton,
    className = _ref.className,
    themeProps = _ref.themeProps,
    _ref$pageSizeOptions = _ref.pageSizeOptions,
    pageSizeOptions = _ref$pageSizeOptions === undefined ? DEFAULT_RANGE_OPTIONS : _ref$pageSizeOptions;
  var _useControllableState = useControllableState({
      controlledValue: currentPageProp,
      defaultValue: initialCurrentPage,
      componentName: 'Pagination',
      stateName: 'currentPage'
    }),
    currentPage = _useControllableState[0],
    setCurrentPage = _useControllableState[1];
  var _useState = useState(currentPage),
    prevCurrentPage = _useState[0],
    setPrevCurrentPage = _useState[1];
  var _useState2 = useState(String(currentPage)),
    inputValue = _useState2[0],
    setInputValue = _useState2[1];
  if (currentPage !== prevCurrentPage) {
    setPrevCurrentPage(currentPage);
    setInputValue(String(currentPage));
  }
  var _useState3 = useState({
      label: "" + (itemsPerPage !== null && itemsPerPage !== undefined ? itemsPerPage : pageSizeOptions[0]),
      value: itemsPerPage !== null && itemsPerPage !== undefined ? itemsPerPage : pageSizeOptions[0]
    }),
    range = _useState3[0],
    setRange = _useState3[1];
  var change = function change(page) {
    var validatedPage = validatePage(page, totalPages);
    onPageChange === null || onPageChange === undefined ? undefined : onPageChange(validatedPage);
    setCurrentPage(validatedPage);
  };
  var next = function next() {
    if (currentPage) {
      change(validatePage(currentPage + 1, totalPages));
    } else {
      change(1);
    }
  };
  var prev = function prev() {
    if (currentPage) {
      change(validatePage(currentPage - 1, totalPages));
    } else {
      change(1);
    }
  };
  var first = function first() {
    change(1);
  };
  var last = function last() {
    return change(totalPages);
  };
  var goToInputValue = function goToInputValue() {
    var page = Number(inputValue);
    // If the input value is invalid, reset it to the current page
    if (!Number.isInteger(page) || page < 1 || page > totalPages) {
      setInputValue(String(currentPage));
      return;
    }
    change(page);
  };
  var onSelect = function onSelect(option) {
    if (option.value) {
      setRange(option);
      setItemPerPage(option.value);
      first();
    }
  };
  var isFirstPage = currentPage === 1;
  var isLastPage = currentPage === totalPages;
  // Left pagination buttons
  var leftPaginationButtons = [{
    icon: jsx(PanelLeftIcon, {}),
    disabled: isFirstPage,
    onClick: first,
    label: 'First Page'
  }, {
    icon: jsx(ChevronLeftIcon, {}),
    disabled: isFirstPage,
    onClick: prev,
    label: 'Previous Page'
  }];
  var rightPaginationButtons = [{
    icon: jsx(ChevronRightIcon, {}),
    disabled: isLastPage,
    onClick: next,
    label: 'Next Page'
  }, {
    icon: jsx(PanelRightIcon, {}),
    disabled: isLastPage,
    onClick: last,
    label: 'Last Page'
  }];
  if (skeleton) {
    var getHeight = function getHeight() {
      if (size === 'small') return elevated ? '40px' : '28px';
      return elevated ? '48px' : '36px';
    };
    return jsx(Skeleton.Rectangle, {
      height: getHeight(),
      width: "400px"
    });
  }
  var handleOnChangeInput = function handleOnChangeInput(e) {
    var value = e.target.value;
    if (isNumeric(value) || value === '') {
      setInputValue(value);
    }
  };
  var handleOnKeydownInput = function handleOnKeydownInput(e) {
    if (e.key === 'Enter') {
      goToInputValue();
    }
  };
  return jsx("nav", {
    "aria-label": "pagination",
    children: jsxs(Flex, {
      gap: size === 'small' ? 8 : 12,
      alignItems: "center",
      className: classNames.apply(undefined, ['cogs-v10', 'cogs-pagination', "cogs-pagination--size-" + size, elevated && 'cogs-pagination--elevated'].concat(getThemeClassNames(inverted, themeProps), [className])),
      children: [jsxs(Flex, {
        gap: size === 'small' ? 4 : 8,
        children: [leftPaginationButtons.map(function (_ref2) {
          var label = _ref2.label,
            disabled = _ref2.disabled,
            onClick = _ref2.onClick,
            icon = _ref2.icon;
          return jsx(Tooltip, {
            content: label,
            children: jsx(Button, {
              icon: icon,
              "aria-disabled": disabled,
              disabled: disabled,
              "aria-label": label,
              size: size,
              onClick: onClick
            })
          }, label);
        }), !hideTeleport && jsx("div", {
          className: "cogs-v10 cogs-pagination-input-wrapper",
          style: {
            width: 20 + totalPages.toString().length * 20 + "px"
          },
          children: jsxs("div", {
            className: "cogs-v10 cogs-pagination-input-content",
            children: [jsx(Input, {
              "aria-label": "Current page",
              type: "number",
              min: 1,
              size: size,
              max: totalPages,
              value: inputValue,
              onChange: handleOnChangeInput,
              onKeyDown: handleOnKeydownInput,
              onBlur: goToInputValue,
              className: "cogs-v10 cogs-pagination-input",
              "aria-valuemax": totalPages,
              textAlign: "right"
            }), jsx("span", {
              className: "cogs-v10 cogs-pagination-separator",
              children: "/"
            }), jsx("span", {
              className: "cogs-v10 cogs-pagination-counter",
              children: totalPages
            })]
          })
        }), rightPaginationButtons.map(function (_ref3) {
          var label = _ref3.label,
            disabled = _ref3.disabled,
            onClick = _ref3.onClick,
            icon = _ref3.icon;
          return jsx(Tooltip, {
            content: label,
            children: jsx(Button, {
              icon: icon,
              size: size,
              disabled: disabled,
              "aria-label": label,
              onClick: onClick
            })
          }, label);
        })]
      }), !hideItemsPerPage && jsxs(Fragment, {
        children: [jsx(Divider, {
          direction: "vertical",
          length: "16px",
          weight: "1px",
          endcap: "round"
        }), jsx(Select, {
          options: pageSizeOptions.map(function (item) {
            return {
              value: item,
              label: "" + item
            };
          }),
          disableTyping: true,
          value: range,
          onChange: onSelect,
          width: 100,
          menuHeader: "Items per page",
          "aria-label": "Items per page"
        })]
      })]
    })
  });
};

export { Pagination, getLastPage };
